import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { useAuth } from "../../hooks/useAuth";
import BrandHeader from "../BrandHeader";
import ResendVerification from "./ResendVerification";

// The link from the verification email lands here, and this page spends the
// token. Two things about that used to hurt, both measured over the 30 days to
// 2026-09-06:
//
//   - The token is single-use and this page burns it, so anything that replayed
//     it told an already-verified visitor "Verification Failed": 15 of 33
//     failures. The server now answers 200 with `already_verified` for a
//     replayed-but-genuine link, which is the `already` state below. Note what
//     reaches it *now*: not a refresh — the token is stripped from the URL
//     below, so a reload has none and lands in `error` — but a second tab, or a
//     mail scanner that opened the link and spent the token first.
//   - A link that really is dead offered one thing: "Back to Login", where the
//     visitor has no password yet and no idea what went wrong. 12 people ended
//     there and were never seen again. The error state now asks for the address
//     and sends a fresh link.
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // `success` is a fallback that nothing reaches today: a genuine activation
  // carries `signed_in` and navigates away, a replay carries `already_verified`.
  // Kept so a server that ever answers neither still renders something sane.
  const [status, setStatus] = useState("verifying"); // verifying | success | already | error
  const [email, setEmail] = useState("");
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  // The token rides in the query string, and analytics captures `$current_url`
  // on every pageview — so it would land in PostHog and in GA4. Since a
  // verified token is now a session, strip it from the address bar before
  // anything reads it. `replaceState` also keeps it out of browser history and
  // out of the Referer of anything this page loads next.
  useEffect(() => {
    if (!token) return;
    const clean = window.location.pathname + window.location.hash;
    // Carry react-router's own history state through rather than nulling it:
    // v7 keeps `{usr, key, idx}` per entry, and wiping it costs the router its
    // bookkeeping for no gain.
    window.history.replaceState(window.history.state, "", clean);
  }, [token]);

  // Ref guard rather than a bare effect: StrictMode double-invokes in
  // development, and the token is single-use, so the second call would race the
  // first and report a failure for a link that just worked. Keyed by token, not
  // a bare boolean, so a different link arriving while this stays mounted is
  // still sent.
  const sentRef = useRef(null);

  // A genuine first click comes back with cookies set, but the auth context was
  // filled at app mount — when this visitor was still logged out — and nothing
  // re-reads it on an in-app navigation. Going straight to /dashboard would hit
  // ProtectedRoute with a stale `user: null` and bounce to /login: exactly the
  // password-less dead end this is meant to remove. So refresh the context
  // first, the way the OAuth callback does, and only then navigate.
  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    if (sentRef.current === token) return;
    sentRef.current = token;
    authApi
      .verifyEmail(token)
      .then((res) => {
        if (res?.data?.signed_in) {
          return checkAuth().then(() => navigate("/dashboard", { replace: true }));
        }
        setStatus(res?.data?.already_verified ? "already" : "success");
        return undefined;
      })
      .catch(() => setStatus("error"));
  }, [token, navigate, checkAuth]);

  const loginLink = (
    <Link to="/login" className="text-[#FF5500] hover:text-[#FF7733] text-sm">
      {t("verifyEmail.logIn")}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
      <div className="w-full max-w-md feature-card p-8 rounded-lg text-center">
        {status === "verifying" && <p className="text-[#A1A1AA]">{t("verifyEmail.verifying")}</p>}
        {status === "success" && (
          <>
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("verifyEmail.successTitle")}</h1>
            <p className="text-[#A1A1AA] mb-6">{t("verifyEmail.successBody")}</p>
            {loginLink}
          </>
        )}
        {status === "already" && (
          <>
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("verifyEmail.alreadyTitle")}</h1>
            <p className="text-[#A1A1AA] mb-6">{t("verifyEmail.alreadyBody")}</p>
            {loginLink}
            {/* The likeliest way to land here is a mail scanner that opened the
                link first and spent the token. For a passkey or lead-capture
                account "log in" is not an answer — there is no password — so
                offer the one door that opens for them too. */}
            <div className="mt-4">
              <Link to="/forgot-password" className="text-sm text-[#A1A1AA] underline underline-offset-2 hover:text-white">
                {t("login.noPasswordCta")}
              </Link>
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("verifyEmail.errorTitle")}</h1>
            <p className="text-[#A1A1AA] mb-4">{t("verifyEmail.errorBody")}</p>
            {/* The address is asked for here rather than carried in the URL: the
                dead link may not even be this visitor's, and a token we could
                not validate is no proof of who is holding it. */}
            <p className="text-[#A1A1AA] text-sm mb-3">{t("verifyEmail.errorHelp")}</p>
            <input
              id="verify-resend-email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t("verifyEmail.emailPlaceholder")}
              aria-label={t("verifyEmail.emailPlaceholder")}
              className="w-full rounded border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-[#6a6a6a] focus:border-[#ff5500] focus:outline-none"
            />
            <ResendVerification email={email} className="mt-3" />
            <div className="mt-6">{loginLink}</div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
